import { pick } from 'lodash';
import { Path } from 'history';
import { PartRequestByLineFilters } from '../stock-part-requests/types/stockPartRequestTypes';
import {
  PartRequestsUrlParamsNames,
  StockPartReqAllStatusEnum,
  StockPartRequestViewEnum,
} from '../stock-part-requests/enums/stockPartRequestsEnum';
import { routesPath } from '../../core/enums/pathEnum';
import { PurchaseUrlParamsNames, StockTabParamEnum } from '../purchase-orders/dashboard/enums/PurchaseOrderStatusEnum';
import { StockPageParamEnums } from '../container/enums/stockEnums';
import { PartRequestSubmitBulkResponse } from './types';
import { BulkRequestTypeEnum } from './BulkRequestTypeEnum';

export type TableFilter = Pick<PartRequestByLineFilters, 'linkedEntityNumber' | 'linkedEntityStatus' | 'productNumber'>;
export type TableFilterKey = keyof TableFilter;

export const tableFilterKeys: readonly TableFilterKey[] = ['linkedEntityNumber', 'linkedEntityStatus', 'productNumber'];

export const isFilterKey = (key: string): key is TableFilterKey => {
  return tableFilterKeys.includes(key as never);
};

export const getTableFilter = (filters: PartRequestByLineFilters): TableFilter => {
  return pick(filters, tableFilterKeys);
};

export const isDefaultFilter = (filters: PartRequestByLineFilters): boolean => {
  return !filters.keyword && !filters.linkedEntityNumber && !filters.linkedEntityStatus && !filters.productNumber;
};

export const getBulkOperationMessage = (type: BulkRequestTypeEnum | null): string | null => {
  switch (type) {
    case BulkRequestTypeEnum.MarkAsCollected:
      return 'Select the lines you want to mark as collected. After confirmation, the selected lines will be moved to Collected status.';
    case BulkRequestTypeEnum.MarkAsReady:
      return 'Select the lines you want to mark as ready. After confirmation, the selected lines will be moved to Ready status.';
    case BulkRequestTypeEnum.ReviewStockSource:
      // eslint-disable-next-line max-len
      return 'Select the lines whose source you want to confirm. After confirmation, the selected lines will be moved to Confirmed status. Please note that if stock is limited, it is allocated top to bottom until it runs out.';
    case BulkRequestTypeEnum.ReviewPricelistSource:
      return 'Select the lines whose source you want to confirm. After confirmation the selected lines will be moved to Confirmed status.';
    case BulkRequestTypeEnum.ReturnToStock:
      // eslint-disable-next-line max-len
      return 'Select the lines you want to return to available stock. After confirmation the shown quantities for the selected lines will be released and will become available in stock again.';
    case BulkRequestTypeEnum.GeneratePurchaseOrder:
      // eslint-disable-next-line max-len
      return 'Select the lines that will be used to create purchase orders. After confirmation, the system will create purchase orders grouped by supplier for the selected lines.';
    default:
      return null;
  }
};

export const getStockPartRequestLinesUrl = (): Partial<Path> => {
  const search = new URLSearchParams({
    [PurchaseUrlParamsNames.PAGE]: StockPageParamEnums.PART_REQUESTS,
    [PurchaseUrlParamsNames.TAB_TYPE]: StockTabParamEnum.ACTIVE,
    [PartRequestsUrlParamsNames.PART_REQ_STATUS]: StockPartReqAllStatusEnum.ALL.toString(),
    view: StockPartRequestViewEnum.LINES,
  });

  return {
    pathname: `/${routesPath.STOCK}`,
    search: search.toString(),
  };
};

export const getStockBulkOperationRedirectUrl = (operation: BulkRequestTypeEnum, result: PartRequestSubmitBulkResponse): Partial<Path> => {
  if (operation === BulkRequestTypeEnum.GeneratePurchaseOrder && result.purchaseOrderId) {
    return { pathname: `/${routesPath.STOCK}/${routesPath.PURCHASE_ORDER}/${result.purchaseOrderId}` };
  }

  return getStockPartRequestLinesUrl();
};
